import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Clock, Instagram, Facebook, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact-messages", form);
      toast.success("Message sent. We'll reply during our next office hours.");
      setForm({ full_name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="section">
        <div className="container-editorial grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Contact</div>
            <h1 className="mt-4 font-serif text-5xl leading-[1.05] text-ink md:text-6xl">
              Come <em className="not-italic text-clay">say hello</em>.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink-muted">
              For general inquiries, partnerships or press. If you're looking for support or applying to a program,
              please use the dedicated forms — they route to the right team faster.
            </p>

            <div className="mt-10 space-y-4">
              <a href="mailto:keyzfoundationinc@gmail.com" data-testid="contact-email-link" className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5 hover:border-clay">
                <Mail className="mt-0.5 h-5 w-5 text-clay" />
                <div>
                  <div className="font-serif text-lg text-ink">Email</div>
                  <div className="text-sm text-ink-muted">keyzfoundationinc@gmail.com</div>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5">
                <Clock className="mt-0.5 h-5 w-5 text-clay" />
                <div>
                  <div className="font-serif text-lg text-ink">Office hours</div>
                  <div className="text-sm text-ink-muted">Monday – Thursday, 8:30 – 4:30</div>
                  <div className="text-sm text-ink-muted">Friday, 8:30 – 12:00</div>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5">
                <MapPin className="mt-0.5 h-5 w-5 text-clay" />
                <div>
                  <div className="font-serif text-lg text-ink">KEYZ Community Center</div>
                  <div className="text-sm text-ink-muted">By appointment · address shared after intake.</div>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="inline-flex items-center gap-2 rounded-full border-2 border-clay/25 px-4 py-2 text-sm font-semibold text-ink hover:border-clay hover:text-clay">
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
                <a href="https://facebook.com" target="_blank" rel="noreferrer" data-testid="contact-facebook" className="inline-flex items-center gap-2 rounded-full border-2 border-clay/25 px-4 py-2 text-sm font-semibold text-ink hover:border-clay hover:text-clay">
                  <Facebook className="h-4 w-4" /> Facebook
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl border border-clay/15 bg-white p-8 md:p-10">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="field-label">Full name *</label>
                  <input required data-testid="contact-name" className="field-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Email *</label>
                  <input required type="email" data-testid="contact-email" className="field-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label">Subject *</label>
                  <input required data-testid="contact-subject" className="field-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label">Message *</label>
                  <textarea required rows={6} data-testid="contact-message" className="field-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <div className="sm:col-span-2 mt-2 flex justify-end">
                  <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary">
                    {loading ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
